"use client";

/**
 * Like + comment on a news article. Likes live at news/{id}/likes/{uid}
 * (one doc per user), comments at news/{id}/comments. Both are world-readable;
 * writes are gated by Firestore rules (own like, own comment; admins moderate).
 */

import { useEffect, useState, useCallback } from "react";
import { db } from "@/lib/firebase";
import {
  collection, doc, onSnapshot, setDoc, deleteDoc, addDoc, updateDoc, deleteDoc as del,
  query, orderBy, Timestamp,
} from "firebase/firestore";
import { useAuth } from "@/contexts/auth-context";
import { useAdmin } from "@/hooks/use-admin";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Heart, MessageCircle, Trash2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface Comment {
  id: string;
  userId: string;
  userName: string;
  userImage?: string;
  text: string;
  createdAt?: any;
}

export default function NewsEngagement({ articleId }: { articleId: string }) {
  const { user, userProfile } = useAuth();
  const { isAdmin } = useAdmin();
  const { toast } = useToast();

  const [likeCount, setLikeCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!articleId) return;
    const unsubLikes = onSnapshot(collection(db, "news", articleId, "likes"), snap => {
      setLikeCount(snap.size);
      setLiked(!!user && snap.docs.some(d => d.id === user.uid));
    });
    const unsubComments = onSnapshot(
      query(collection(db, "news", articleId, "comments"), orderBy("createdAt", "desc")),
      snap => setComments(snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Comment, "id">) }))),
    );
    return () => { unsubLikes(); unsubComments(); };
  }, [articleId, user]);

  const toggleLike = useCallback(async () => {
    if (!user) { toast({ title: "Sign in to like", variant: "destructive" }); return; }
    const ref = doc(db, "news", articleId, "likes", user.uid);
    try {
      if (liked) await deleteDoc(ref);
      else await setDoc(ref, { at: Timestamp.now() });
    } catch { toast({ title: "Could not update like", variant: "destructive" }); }
  }, [user, liked, articleId, toast]);

  const postComment = useCallback(async () => {
    if (!user) { toast({ title: "Sign in to comment", variant: "destructive" }); return; }
    const body = text.trim();
    if (!body) return;
    setPosting(true);
    try {
      await addDoc(collection(db, "news", articleId, "comments"), {
        userId: user.uid,
        userName: userProfile?.displayName || user.email || "User",
        userImage: userProfile?.profileImageUrl || "",
        text: body.slice(0, 2000),
        createdAt: Timestamp.now(),
      });
      setText("");
    } catch { toast({ title: "Could not post comment", variant: "destructive" }); }
    finally { setPosting(false); }
  }, [user, userProfile, text, articleId, toast]);

  const removeComment = useCallback(async (c: Comment) => {
    try { await del(doc(db, "news", articleId, "comments", c.id)); }
    catch { toast({ title: "Could not delete", variant: "destructive" }); }
  }, [articleId, toast]);

  const fmt = (ts: any) => {
    const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
    return d ? d.toLocaleDateString() : "";
  };

  return (
    <div className="mt-8 border-t border-border/50 pt-6 space-y-5">
      {/* Like + comment count bar */}
      <div className="flex items-center gap-3">
        <Button
          variant={liked ? "default" : "outline"}
          size="sm"
          onClick={toggleLike}
          className={cn("gap-1.5", liked && "bg-rose-600 hover:bg-rose-500 text-white")}
        >
          <Heart className={cn("h-4 w-4", liked && "fill-current")} /> {likeCount}
        </Button>
        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MessageCircle className="h-4 w-4" /> {comments.length} comment{comments.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Composer */}
      <div className="space-y-2">
        <Textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={user ? "Add a comment…" : "Sign in to comment"}
          disabled={!user || posting}
          rows={2}
          className="resize-y"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={postComment} disabled={!user || posting || !text.trim()} className="gap-1.5">
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} Post
          </Button>
        </div>
      </div>

      {/* Comments */}
      <div className="space-y-3">
        {comments.map(c => (
          <div key={c.id} className="flex gap-3">
            <div className="h-8 w-8 rounded-full bg-muted overflow-hidden shrink-0">
              {c.userImage
                // eslint-disable-next-line @next/next/no-img-element
                ? <img src={c.userImage} alt={c.userName} className="h-full w-full object-cover" />
                : <div className="h-full w-full flex items-center justify-center text-xs font-semibold text-muted-foreground">{(c.userName || "U")[0]?.toUpperCase()}</div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{c.userName}</span>
                <span className="text-[11px] text-muted-foreground">{fmt(c.createdAt)}</span>
                {(isAdmin || c.userId === user?.uid) && (
                  <button onClick={() => removeComment(c)} className="ml-auto text-muted-foreground hover:text-destructive" aria-label="Delete comment">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <p className="text-sm text-foreground/90 whitespace-pre-wrap break-words">{c.text}</p>
            </div>
          </div>
        ))}
        {comments.length === 0 && (
          <p className="text-sm text-muted-foreground">Be the first to comment.</p>
        )}
      </div>
    </div>
  );
}
